import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { canAccessScreen, SCREENS } from '@/config/screens'
import { i18n } from '@/i18n'
import ToastEventBus from 'primevue/toasteventbus'

const AppLayout = () => import('@/layouts/AppLayout.vue')
const NotFound = () => import('@/views/NotFound.vue')
const Login = () => import('@/views/auth/Login.vue')
const Register = () => import('@/views/auth/Register.vue')
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/views/auth/ResetPassword.vue')
const Dashboard = () => import('@/views/Dashboard.vue')
const SowList = () => import('@/views/herd/SowList.vue')
const SowCardDetail = () => import('@/views/herd/SowCardDetail.vue')
const FarmMap = () => import('@/views/herd/FarmMap.vue')
const PenMovementList = () => import('@/views/herd/PenMovementList.vue')
const FarrowingList = () => import('@/views/reproduction/FarrowingList.vue')
const SowFarrowingTransferList = () => import('@/views/reproduction/SowFarrowingTransferList.vue')
const BoarList = () => import('@/views/herd/BoarList.vue')
const SemenCollectionList = () => import('@/views/reproduction/SemenCollectionList.vue')
const InseminationList = () => import('@/views/reproduction/InseminationList.vue')
const FosteringList = () => import('@/views/reproduction/FosteringList.vue')
const PigIntakeList = () => import('@/views/herd/PigIntakeList.vue')
const SupplierManagement = () => import('@/views/herd/SupplierManagement.vue')
const SaleLots = () => import('@/views/SaleLots.vue')
const SaleLotList = () => import('@/views/sales/SaleLotList.vue')
const SaleOrderList = () => import('@/views/sales/SaleOrderList.vue')
const UserManagement = () => import('@/views/admin/UserManagement.vue')
const RoleManagement = () => import('@/views/admin/RoleManagement.vue')
const FarmKeyManagement = () => import('@/views/admin/FarmKeyManagement.vue')
const SettingsManagement = () => import('@/views/admin/SettingsManagement.vue')
const WallSettings = () => import('@/views/admin/WallSettings.vue')
const NotifySettings = () => import('@/views/admin/NotifySettings.vue')
const SiteSettingsManagement = () => import('@/views/admin/SiteSettingsManagement.vue')
const CameraList = () => import('@/views/camera/CameraList.vue')
const CameraAIEvents = () => import('@/views/camera/CameraAIEvents.vue')
const ClimateMonitor = () => import('@/views/climate/ClimateMonitor.vue')
const ClimateThresholds = () => import('@/views/climate/ClimateThresholds.vue')
const ClimateDevices = () => import('@/views/climate/ClimateDevices.vue')
const ClimateOverview = () => import('@/views/climate/ClimateOverview.vue')
const ClimateAlerts = () => import('@/views/climate/ClimateAlerts.vue')
const ClimatePLCRegisters = () => import('@/views/climate/ClimatePLCRegisters.vue')
const FeedFormulaList = () => import('@/views/operations/FeedFormulaList.vue')
const FeedAssessment = () => import('@/views/operations/FeedAssessment.vue')
const MedicationList = () => import('@/views/operations/MedicationList.vue')
const MedicalRecordList = () => import('@/views/operations/MedicalRecordList.vue')
const VaccineScheduleList = () => import('@/views/operations/VaccineScheduleList.vue')
const InventoryList = () => import('@/views/inventory/InventoryList.vue')
const BarnManagement = () => import('@/views/infrastructure/BarnManagement.vue')
const PenManagement = () => import('@/views/infrastructure/PenManagement.vue')
const LifecycleManagement = () => import('@/views/lifecycle/LifecycleManagement.vue')
const DailyTaskList = () => import('@/views/tasks/DailyTaskList.vue')
const PlaceholderView = () => import('@/views/PlaceholderView.vue')
const More = () => import('@/views/mobile/More.vue')
const Drafts = () => import('@/views/mobile/Drafts.vue')
const Alerts = () => import('@/views/mobile/Alerts.vue')
const Home = () => import('@/views/Home.vue')
const HomeArticle = () => import('@/views/HomeArticle.vue')
const WallBoard = () => import('@/views/WallBoard.vue')
const CameraAILogsheet = () => import('@/views/camera/CameraAILogsheet.vue')
const InstallApp = () => import('@/views/InstallApp.vue')
const PreviewEntry =() => import('@/views/PreviewEntry.vue')

// Routes still pending a real CRUD implementation. Each renders the same
// PlaceholderView shell (page header + empty-state Card) so the sidebar/menu,
// breadcrumb and permission wiring is complete for every function group
// (F1–F13) from day one, even where the backend API doesn't exist yet.
const placeholders = [
  { name: 'FarmList', path: 'admin/farms', label: 'Trang trại', description: 'Quản lý các trang trại và thông tin liên quan.', icon: 'pi pi-map-marker' },
  { name: 'PigBatchList', path: 'herd/pig-batches', label: 'Lứa heo (batch)', description: 'Theo dõi các lứa heo theo chuồng.', icon: 'pi pi-sitemap' },
  { name: 'ReproductiveCycleList', path: 'reproduction/cycles', label: 'Chu kỳ sinh sản', description: 'Theo dõi chu kỳ động dục của heo nái.', icon: 'pi pi-heart' },
  { name: 'SalesRecordList', path: 'operations/sales-records', label: 'Bán hàng', description: 'Ghi nhận các đợt xuất bán heo.', icon: 'pi pi-calendar-clock' },
  { name: 'TaskList', path: 'tasks/todo', label: 'Việc theo mốc D/F', description: 'Công việc sinh tự động theo mốc ngày (D96/D100/D107, F+1…F+22).', icon: 'pi pi-list-check' },
  { name: 'AlertList', path: 'tasks/alerts', label: 'Cảnh báo cần xử lý', description: 'Cảnh báo vàng/đỏ theo mốc cai sữa, bỏ ăn, thiết bị lỗi.', icon: 'pi pi-exclamation-triangle' },
  { name: 'CleaningScheduleList', path: 'biosecurity/cleaning-schedule', label: 'Lịch vệ sinh', description: 'Lịch sát trùng, dội vôi, cọ máng theo tuần.', icon: 'pi pi-shield' },
  { name: 'BiosecurityLogList', path: 'biosecurity/logs', label: 'Nhà sát trùng', description: 'Đối soát rửa tay cồn và trang phục bảo hộ qua nhà sát trùng.', icon: 'pi pi-shield' },
  { name: 'SecurityLogList', path: 'biosecurity/security', label: 'An ninh', description: 'Giám sát an ninh 2 cổng và khu vực phối trộn cám.', icon: 'pi pi-shield' },
  { name: 'AlarmDeviceList', path: 'sound/alarm-devices', label: 'Còi / loa cảnh báo', description: 'Danh mục còi, loa cảnh báo theo khu.', icon: 'pi pi-volume-up' },
  { name: 'SoundScheduleConfig', path: 'sound/schedule', label: 'Lịch phát nhạc', description: 'Cấu hình khung giờ phát nhạc qua rơ-le hẹn giờ.', icon: 'pi pi-volume-up' },
  { name: 'RedLightConfig', path: 'sound/red-light', label: 'Đèn đỏ D19→D23', description: 'Vận hành đèn đỏ tăng cường trong cửa sổ gom lên giống.', icon: 'pi pi-volume-up' },
  { name: 'FCRForecast', path: 'reports/fcr-forecast', label: 'Dự báo FCR / ngày xuất', description: 'Dự báo FCR và ngày xuất chuồng bằng Predictive Analytics.', icon: 'pi pi-chart-bar' }
]

const routes = [
  { path: '/install', name: 'InstallApp', component: InstallApp, meta: { requiresAuth: false } },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  { path: '/lo-heo', name: 'SaleLots', component: SaleLots, meta: { requiresAuth: false, label: 'Lô heo bán' } },
  { path: '/register', name: 'Register', component: Register, meta: { requiresAuth: false } },
  { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword, meta: { requiresAuth: false } },
  { path: '/reset-password', name: 'ResetPassword', component: ResetPassword, meta: { requiresAuth: false } },
  {
    path: '/home',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: false }
  },
  // Bài viết của trang chủ công khai (src/content/articles.js).
  { path: '/home/tin-tuc/:slug', name: 'HomeArticle', component: HomeArticle, meta: { requiresAuth: false } },
  // Dev-only mockup entry: /preview/:role logs in a fake session (see
  // authStore.loginMock) and drops into the normal authenticated app, so the
  // dashboard/menu/permissions can be reviewed without a running backend.
  // import.meta.env.DEV is false in a production build, so this route (and
  // the login bypass it relies on) never ships.
  ...(import.meta.env.DEV ? [{
    path: '/preview/:role?',
    name: 'PreviewEntry',
    component: PreviewEntry,
    meta: { requiresAuth: false }
  }] : []),
  // Màn hình giám sát toàn màn hình cho màn lớn (không thanh menu).
  { path: '/wall', name: 'WallBoard', component: WallBoard, meta: { requiresAuth: true, label: 'Màn hình giám sát' } },
  { path: '/camera-ai/logsheet', name: 'CameraAILogsheet', component: CameraAILogsheet, meta: { requiresAuth: true, label: 'Logsheet Camera AI' } },
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      { path: 'more', name: 'MobileMore', component: More, meta: { label: 'Chức năng' } },
      { path: 'drafts', name: 'Drafts', component: Drafts, meta: { label: 'Bản nháp' } },
      { path: 'alerts', name: 'MobileAlerts', component: Alerts, meta: { label: 'Cảnh báo' } },
      { path: '', name: 'Dashboard', component: Dashboard, meta: { label: 'Tổng quan' } },
      { path: 'herd/intakes', name: 'PigIntakeList', component: PigIntakeList, meta: { label: 'Nhập heo' } },
      { path: 'herd/sows', name: 'SowList', component: SowList, meta: { label: 'Heo nái' } },
      { path: 'herd/sows/:id', name: 'SowCardDetail', component: SowCardDetail, meta: { label: 'Thẻ nái' } },
      { path: 'herd/boars', name: 'BoarList', component: BoarList, meta: { label: 'Heo đực' } },
      { path: 'reproduction/semen-collections', name: 'SemenCollectionList', component: SemenCollectionList, meta: { label: 'Thu tinh dịch' } },
      { path: 'reproduction/inseminations', name: 'InseminationList', component: InseminationList, meta: { label: 'Phối giống' } },
      { path: 'herd/farm-map', name: 'FarmMap', component: FarmMap, meta: { label: 'Bản đồ số trại' } },
      { path: 'herd/pen-movements', name: 'PenMovementList', component: PenMovementList, meta: { label: 'Di chuyển chuồng' } },
      { path: 'herd/suppliers', name: 'SupplierManagement', component: SupplierManagement, meta: { label: 'Nhà cung cấp' } },
      { path: 'sales/lots', name: 'SaleLotList', component: SaleLotList, meta: { label: 'Lô heo bán' } },
      { path: 'sales/orders', name: 'SaleOrderList', component: SaleOrderList, meta: { label: 'Đơn đặt mua' } },
      { path: 'reproduction/farrowing-transfers', name: 'SowFarrowingTransferList', component: SowFarrowingTransferList, meta: { label: 'Chuyển chuồng nái đẻ' } },
      { path: 'reproduction/farrowings', name: 'FarrowingList', component: FarrowingList, meta: { label: 'Đẻ' } },
      { path: 'reproduction/fosterings', name: 'FosteringList', component: FosteringList, meta: { label: 'Ghép bầy' } },
      { path: 'admin/users', name: 'UserManagement', component: UserManagement, meta: { label: 'Người dùng' } },
      { path: 'admin/roles', name: 'RoleManagement', component: RoleManagement, meta: { label: 'Vai trò & Phân quyền' } },
      { path: 'pigs/lifecycle', name: 'LifecycleManagement', component: LifecycleManagement, meta: { label: 'Vòng đời heo' } },
      { path: 'tasks/daily', name: 'DailyTaskList', component: DailyTaskList, meta: { label: 'Nhiệm vụ hàng ngày' } },
      { path: 'camera-ai/cameras', name: 'CameraList', component: CameraList, meta: { label: 'Camera' } },
      { path: 'camera-ai/alerts', name: 'CameraAlertList', component: CameraAIEvents, meta: { label: 'Camera AI' } },
      { path: 'climate/overview', name: 'ClimateOverview', component: ClimateOverview, meta: { label: 'Khí hậu' } },
      { path: 'climate/monitor', name: 'ClimateMonitor', component: ClimateMonitor, meta: { label: 'Giám sát khí hậu' } },
      // Merged into Giám sát khí hậu (tab Nhật ký đo); kept so old links and screen names still resolve.
      { path: 'climate/logs', name: 'ClimateLogList', redirect: { name: 'ClimateMonitor', query: { tab: 'logs' } }, meta: { label: 'Tiểu khí hậu' } },
      { path: 'climate/thresholds', name: 'ClimateThresholdList', component: ClimateThresholds, meta: { label: 'Ngưỡng tiểu khí hậu' } },
      { path: 'climate/devices', name: 'ClimateDeviceList', component: ClimateDevices, meta: { label: 'Thiết bị khí hậu' } },
      { path: 'climate/plc', name: 'ClimatePLCRegisters', component: ClimatePLCRegisters, meta: { label: 'Bảng thanh ghi PLC' } },
      { path: 'climate/alerts', name: 'ClimateAlerts', component: ClimateAlerts, meta: { label: 'Cảnh báo khí hậu' } },
      // Replaced by Cảnh báo khí hậu; kept so old links and screen names still resolve.
      { path: 'climate/health', name: 'ClimateInfraHealth', redirect: { name: 'ClimateAlerts' }, meta: { label: 'Sức khoẻ hạ tầng' } },
      { path: 'operations/feeding-logs', name: 'FeedingLogList', component: FeedAssessment, meta: { label: 'Chấm cám' } },
      { path: 'operations/vaccine-schedules', name: 'VaccineScheduleList', component: VaccineScheduleList, meta: { label: 'Lịch tiêm phòng' } },
      { path: 'operations/medical-records', name: 'MedicalRecordList', component: MedicalRecordList, meta: { label: 'Bệnh án điện tử' } },
      { path: 'operations/medications', name: 'MedicationList', component: MedicationList, meta: { label: 'Bảng thuốc' } },
      { path: 'operations/rations', name: 'FeedRationList', redirect: { name: 'FeedingLogList' } },
      { path: 'infrastructure/feed-formulas', name: 'FeedFormulaList', component: FeedFormulaList, meta: { label: 'Công thức thức ăn' } },
      { path: 'inventory/stock', name: 'InventoryList', component: InventoryList, meta: { label: 'Tồn kho' } },
      { path: 'infrastructure/barns', name: 'BarnList', component: BarnManagement, meta: { label: 'Chuồng trại' } },
      { path: 'infrastructure/pens', name: 'PenList', component: PenManagement, meta: { label: 'Ô chuồng' } },
      { path: 'admin/farm-keys', name: 'FarmKeyManagement', component: FarmKeyManagement, meta: { label: 'Farm Key' } },
      { path: 'admin/wall-settings', name: 'WallSettings', component: WallSettings, meta: { label: 'Auto play màn hình lớn' } },
      { path: 'admin/notify-settings', name: 'NotifySettings', component: NotifySettings, meta: { label: 'Thông báo đẩy' } },
      { path: 'admin/settings', name: 'SettingsManagement', component: SettingsManagement, meta: { label: 'Cài đặt hệ thống' } },
      { path: 'admin/site-settings', name: 'SiteSettingsManagement', component: SiteSettingsManagement, meta: { label: 'Cài đặt trang' } },
      ...placeholders.map((p) => ({
        path: p.path,
        name: p.name,
        component: PlaceholderView,
        meta: { label: p.label, description: p.description, icon: p.icon, comingSoon: true }
      }))
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { requiresAuth: false, label: 'Không tìm thấy trang' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Public pages (homepage, articles): links to a section (#gia-heo) scroll to
  // it under the sticky header, a new page starts at the top. The app's own
  // screens keep their scroll as before.
  scrollBehavior(to, from, saved) {
    if (saved) return saved
    if (to.hash) return { el: to.hash, top: 72, behavior: 'smooth' }
    if (to.meta.requiresAuth === false && to.path !== from.path) return { top: 0 }
    return false
  }
})

// TEMPORARY: skip login while the UI is being built. Signs in a demo admin
// session automatically. Set to false to restore the normal login flow.
// The real backend is wired up now (see AuthService.Login) — its JWTs are
// signed and role-based, so the old fake "mock-token" session gets rejected
// by every protected endpoint (401 loop). Real login flow only from here on.
const SKIP_LOGIN = false

// Toast from outside a component (the guard runs outside setup). <Toast /> is
// mounted in App.vue. De-duplicated so a redirect chain shows one toast.
let lastNoAccessAt = 0
let appReady = false
function notifyNoAccess() {
  const now = Date.now()
  if (now - lastNoAccessAt < 800) return
  lastNoAccessAt = now
  const show = () => ToastEventBus.emit('add', { severity: 'warn', summary: i18n.global.t('common.noAccess'), life: 3000 })
  // Mở thẳng bằng đường dẫn: <Toast /> chưa được gắn nên thông báo sẽ mất; hoãn tới khi app đã dựng xong.
  if (appReady) show(); else setTimeout(show, 900)
}

router.beforeEach((to) => {
  const authStore = useAuthStore()

  if (SKIP_LOGIN && to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    authStore.loginMock('admin')
  }
  if (SKIP_LOGIN && to.name === 'Login') {
    return { name: 'Dashboard' }
  }

  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    // Default entry point (bare "/") lands on the public homepage rather than
    // straight at the login form; every other deep link still goes to Login
    // with a redirect back so it lands where the user meant to go.
    if (to.name === 'Dashboard') {
      return { name: 'Home' }
    }
    return { name: 'Login', query: { redirect: to.fullPath } }
  }
  if (['Login', 'Register', 'ForgotPassword'].includes(to.name) && authStore.isAuthenticated) {
    return { name: 'Dashboard' }
  }
  if (to.meta.requiresAuth !== false && to.name && !canAccessScreen(authStore.permissions, to.name)) {
    if (import.meta.env.DEV && !(to.name in SCREENS)) {
      console.error('[router] route "' + String(to.name) + '" is missing from src/config/screens.js')
    }
    notifyNoAccess()
    return { name: 'Dashboard', replace: true }
  }
  return true
})

// Tiêu đề tab trình duyệt đổi theo màn: "<Tên màn> — SmartFarm".
function applyTitle(to) {
  const key = `nav.${String(to.name)}.label`
  const { t, te } = i18n.global
  const label = te(key) ? t(key) : (to.meta?.label || '')
  const base = 'SmartFarm'
  document.title = label ? `${label} — ${base}` : `${base} — Quản lý trang trại`
}
router.afterEach(applyTitle)
// Đổi ngôn ngữ thì tiêu đề tab đổi theo ngay.
watch(() => i18n.global.locale.value, () => applyTitle(router.currentRoute.value))

router.isReady().then(() => setTimeout(() => { appReady = true }, 1200))

export default router
